import React, { forwardRef } from 'react'
// PRO-130: the deep `antd/es/select` subpath, NOT the `antd` root barrel.
import AntdSelect from 'antd/es/select'
import type { RefSelectProps, SelectProps } from 'antd/es/select'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Select as AntdSeamSelect } from './Select.antd'
import { SelectShadcn } from './Select.shadcn'
import type { TSelect } from './types'

export type { TSelect }

/**
 * Compat seam for antd's `Select` — and, with `../Dropdown`, the components
 * put behind the design toggle after `../Button` (#970), `../Typography` +
 * `../Space` (#971), `../Tag` (#972), the presentational batch (#975),
 * `../Tooltip` + `../Modal` (#982), `../Spin` (#983) and the login surface
 * (#984).
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders
 * `./Select.antd` with the props and the ref it was given: same element, same
 * class names, same DOM. The only difference from the previous
 * `src/antd/Select.tsx` — which IS `./Select.antd`, moved — is one extra
 * component in the React tree, which renders nothing of its own.
 * `../__tests__/select-seam-parity.test.tsx` asserts that node for node across
 * a prop matrix, and it is the test to keep green.
 *
 * READ `./Select.antd` BEFORE ANYTHING ELSE HERE. `src/antd` has always
 * SHADOWED Select rather than re-exported it (one of the six — see
 * AGENTS.md): the seam defaults `getPopupContainer` to the trigger's parent,
 * which for a Select is the control's own root — so the popup is rendered
 * INSIDE the control rather than portalled to the body.
 * "Unchanged with the toggle off" therefore means unchanged versus that seam,
 * never versus `antd/es/select`, and the parity test compares against
 * `./Select.antd` for exactly that reason. The 23 files still importing
 * `Select` from raw `antd` are the documented deferred residual; the toggle
 * does not reach them, by design.
 *
 * ---------------------------------------------------------------------------
 * WHY SELECT AND DROPDOWN TOGETHER
 *
 * Both are POPUP surfaces with the same two questions to answer — where does
 * the popup mount, and at what z-index — and the Tooltip/Modal batch already
 * answered them once for the two PORTAL seams. Porting these two in one pass
 * means the answer is given once here as well, rather than twice by two lanes.
 * It is a DIFFERENT answer from Tooltip's, and `./Select.shadcn` says at
 * length why: wherever antd already put it — inside the control for a Select,
 * the trigger's parent for a Dropdown — because 23 call sites and a dozen
 * stylesheets were written against those defaults.
 * `src/components/ui/overlay-panel.ts` holds the z-index half — reused from
 * that batch rather than re-derived, so the whole overlay scale now sits in
 * one file.
 *
 * ---------------------------------------------------------------------------
 * THE STATICS ARE NOT DISPATCHED, and cannot be
 *
 * `Select.Option` and `Select.OptGroup` are read at the CALL SITE
 * (`<Select.Option>`), where no hook can run — the same problem
 * `../Modal`'s imperative `Modal.confirm` has. It needs no solution here:
 * rc-select's `Option`/`OptGroup` are inert placeholder components that render
 * `null` and carry only the two marker flags (`isSelectOption`,
 * `isSelectOptGroup`) that a Select reads off its children. `./options`
 * reads those same flags, so ONE marker serves both branches and the 16
 * `<Select.Option>` tags in this repo cannot end up bound to the wrong mode.
 * `SECRET_COMBOBOX_MODE_DO_NOT_USE` is forwarded for the same reason: the
 * shape of the export must not change.
 */
const SelectDispatcher = forwardRef<RefSelectProps, SelectProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') return <SelectShadcn {...props} ref={ref} />

    return <AntdSeamSelect {...props} ref={ref} />
  }
)

SelectDispatcher.displayName = 'Select'

const Select = SelectDispatcher as unknown as TSelect

Select.SECRET_COMBOBOX_MODE_DO_NOT_USE =
  AntdSelect.SECRET_COMBOBOX_MODE_DO_NOT_USE
Select.Option = AntdSelect.Option
Select.OptGroup = AntdSelect.OptGroup

export { Select }
